import { Component } from '@theme/component';

/**
 * Hall of Fame page.
 *
 * - Spotlight deck: rotates through every 1st-place winner. The rotation is
 *   driven by the active dot's CSS progress animation (`animationend`), so
 *   hover/focus pausing and reduced motion come from CSS for free.
 * - Category tabs: one panel visible at a time, deep-linkable by hash.
 *   With `data-hide-winners`, no panel shows until a category is chosen
 *   (picker card, tab, spotlight card, hero button, hash or editor).
 * - Lightbox: view any winner image large, step through the category,
 *   and download it (Shopify CDN is cross-origin, so we fetch a blob).
 *
 * @typedef {{
 *   stage?: HTMLElement,
 *   cards?: HTMLAnchorElement[],
 *   dots?: HTMLButtonElement[],
 *   tabbar?: HTMLElement,
 *   tabs?: HTMLButtonElement[],
 *   panels?: HTMLElement[],
 *   lightbox: HTMLDialogElement,
 *   lightboxImage: HTMLImageElement,
 *   lightboxLabel: HTMLElement,
 *   lightboxPrev: HTMLButtonElement,
 *   lightboxNext: HTMLButtonElement,
 *   lightboxDownload: HTMLButtonElement,
 * }} Refs
 *
 * @extends {Component<Refs>}
 */
class HallOfFameComponent extends Component {
  #active = 0;
  /** @type {HTMLElement[]} */
  #shots = [];
  #shotIndex = 0;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.dataset.ready = '';
    this.#layoutDeck();

    this.addEventListener('animationend', this.#onProgressEnd, { signal });
    document.addEventListener('visibilitychange', this.#syncPaused, { signal });
    window.addEventListener('hashchange', () => this.#openFromHash(true), { signal });
    document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });

    this.refs.lightbox?.addEventListener(
      'click',
      (event) => {
        if (event.target === this.refs.lightbox) this.closeLightbox();
      },
      { signal }
    );
    this.refs.lightbox?.addEventListener('keydown', this.#onLightboxKeydown, { signal });

    for (const link of this.querySelectorAll('[data-hof-scroll]')) {
      link.addEventListener('click', this.#onCtaClick, { signal });
    }

    // Only spend autoplay time while the stage is actually on screen.
    if (this.refs.stage && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(([entry]) => {
        this.toggleAttribute('data-offscreen', !entry?.isIntersecting);
        this.#syncPaused();
      });
      this.#observer.observe(this.refs.stage);
    }

    this.#openFromHash(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#observer?.disconnect();
  }

  /* ---------------- Spotlight deck ---------------- */

  get #cards() {
    return this.refs.cards ?? [];
  }

  /** @param {number} index */
  #showChampion(index) {
    const count = this.#cards.length;
    if (!count) return;

    this.#active = ((index % count) + count) % count;
    this.#layoutDeck();
  }

  #layoutDeck() {
    const count = this.#cards.length;

    this.#cards.forEach((card, i) => {
      const offset = (i - this.#active + count) % count;
      let pos = String(offset);
      if (offset === count - 1 && count > 2) pos = '-1';
      if (offset > 1 && offset !== count - 1) pos = 'hidden';

      card.dataset.pos = pos;
      const isFront = offset === 0;
      card.tabIndex = isFront ? 0 : -1;
      card.toggleAttribute('aria-hidden', !isFront);
    });

    for (const caption of this.querySelectorAll('[data-hof-caption]')) {
      caption.classList.toggle('is-active', Number(/** @type {HTMLElement} */ (caption).dataset.hofCaption) === this.#active);
    }

    // One nav button per category: ties in 1st place share their category's button.
    const activeCat = Number(this.#cards[this.#active]?.dataset.hofCat ?? 0);
    (this.refs.dots ?? []).forEach((dot, i) => {
      const isActive = i === activeCat;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-pressed', String(isActive));
      // Restart the progress animation on the newly active dot.
      const fill = dot.querySelector('[data-hof-progress]');
      if (fill instanceof HTMLElement && isActive) {
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }
    });
  }

  /** @param {AnimationEvent} event */
  #onProgressEnd = (event) => {
    if (event.animationName !== 'hof-progress' || this.dataset.autoplay !== 'on') return;
    this.#showChampion(this.#active + 1);
  };

  #syncPaused = () => {
    this.toggleAttribute('data-paused', document.hidden || this.hasAttribute('data-offscreen'));
  };

  #stopAutoplay() {
    this.dataset.autoplay = 'off';
  }

  /** @param {MouseEvent} event */
  onDotClick(event) {
    const dot = /** @type {HTMLElement} */ (event.target);
    this.#stopAutoplay();
    const index = this.#cards.findIndex((card) => card.dataset.hofCat === dot.dataset.hofDot);
    if (index >= 0) this.#showChampion(index);
  }

  /** @param {MouseEvent} event */
  onCardClick(event) {
    const card = /** @type {HTMLAnchorElement} */ (event.target);
    event.preventDefault();

    if (card.dataset.pos !== '0') {
      // A card peeking from behind: bring it to the front first.
      this.#stopAutoplay();
      this.#showChampion(Number(card.dataset.hofChamp));
      return;
    }

    const blockId = card.dataset.hofPanelTarget;
    if (blockId) this.#selectPanel(blockId, { scroll: true, focus: false, updateHash: true });
  }

  /** @param {MouseEvent} event */
  #onCtaClick = (event) => {
    event.preventDefault();
    const card = this.#cards[this.#active];
    const blockId = card?.dataset.hofPanelTarget;
    if (blockId) this.#selectPanel(blockId, { scroll: true, focus: false, updateHash: false });
  };

  /* ---------------- Category tabs ---------------- */

  /**
   * @param {string} blockId
   * @param {{ scroll?: boolean, focus?: boolean, updateHash?: boolean, animate?: boolean }} [options]
   */
  #selectPanel(blockId, { scroll = false, focus = false, updateHash = false, animate = true } = {}) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    if (!panels.some((panel) => panel.dataset.hofPanel === blockId)) return;
    this.#reveal();
    let target = null;

    for (const panel of panels) {
      const isActive = panel.dataset.hofPanel === blockId;
      const wasActive = panel.classList.contains('is-active');
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        target = panel;
        if (animate && !wasActive) this.#playEntrance(panel);
      }
    }

    if (!target) return;

    for (const tab of tabs) {
      const isActive = tab.dataset.hofTab === blockId;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive) {
        if (focus) tab.focus({ preventScroll: true });
      }
    }

    if (updateHash) history.replaceState(null, '', `#${target.id}`);

    // Keep the active tab visible inside the horizontally scrolling tab bar.
    const activeTab = tabs.find((tab) => tab.dataset.hofTab === blockId);
    const list = activeTab?.parentElement;
    if (activeTab && list) {
      list.scrollLeft = activeTab.offsetLeft - (list.clientWidth - activeTab.offsetWidth) / 2;
    }

    for (const pick of this.querySelectorAll('[data-hof-pick]')) {
      pick.setAttribute('aria-expanded', String(/** @type {HTMLElement} */ (pick).dataset.hofPick === blockId));
    }

    if (scroll) {
      const winners = this.querySelector('.hof-winners');
      winners?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  /** Shows tabs and panels when winners start hidden behind the category picker. */
  #reveal() {
    if (this.hasAttribute('data-hide-winners')) this.toggleAttribute('data-revealed', true);
  }

  /** @param {MouseEvent} event */
  onPickClick(event) {
    const pick = /** @type {HTMLElement} */ (event.target);
    const blockId = pick.dataset.hofPick;
    if (blockId) this.#selectPanel(blockId, { scroll: true, focus: true, updateHash: true });
  }

  /** @param {HTMLElement} panel */
  #playEntrance(panel) {
    panel.classList.remove('is-entering');
    void panel.offsetWidth;
    panel.classList.add('is-entering');
    window.setTimeout(() => panel.classList.remove('is-entering'), 2200);
  }

  /** @param {MouseEvent} event */
  onTabClick(event) {
    const tab = /** @type {HTMLElement} */ (event.target);
    if (tab.dataset.hofTab) this.#selectPanel(tab.dataset.hofTab, { updateHash: true });
  }

  /** @param {KeyboardEvent} event */
  onTabKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (current < 0) return;

    /** @type {Record<string, number>} */
    const moves = { ArrowRight: current + 1, ArrowLeft: current - 1, Home: 0, End: tabs.length - 1 };
    if (!(event.key in moves)) return;

    event.preventDefault();
    const next = tabs[(Number(moves[event.key]) + tabs.length) % tabs.length];
    if (next?.dataset.hofTab) this.#selectPanel(next.dataset.hofTab, { focus: true, updateHash: true });
  }

  /** @param {boolean} scroll */
  #openFromHash(scroll) {
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    // Short ids (#hof-sales) are current; the long section-scoped ids are kept for links shared earlier.
    const panel = (this.refs.panels ?? []).find((p) => p.id === id || p.dataset.hofLegacyId === id);
    if (panel?.dataset.hofPanel) {
      this.#selectPanel(panel.dataset.hofPanel, { scroll, animate: scroll });
      if (!scroll) requestAnimationFrame(() => this.querySelector('.hof-winners')?.scrollIntoView());
    }
  }

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    const blockId = /** @type {CustomEvent} */ (event).detail?.blockId;
    if (!blockId) return;
    // A category selects its own panel; a winner selects the panel it lives in.
    const panel =
      (this.refs.panels ?? []).find((p) => p.dataset.hofPanel === blockId) ??
      this.querySelector(`[data-hof-block="${CSS.escape(blockId)}"]`)?.closest('.hof-panel');
    if (panel instanceof HTMLElement && panel.dataset.hofPanel) {
      this.#selectPanel(panel.dataset.hofPanel, { animate: false });
    }
  };

  /* ---------------- Lightbox ---------------- */

  /** @param {MouseEvent} event */
  openLightbox(event) {
    const shot = /** @type {HTMLElement} */ (event.target);
    const panel = shot.closest('.hof-panel');
    this.#shots = panel ? Array.from(panel.querySelectorAll('[data-hof-full]')) : [shot];
    this.#shotIndex = Math.max(0, this.#shots.indexOf(shot));
    this.#renderShot();

    const { lightbox, lightboxPrev, lightboxNext } = this.refs;
    const single = this.#shots.length < 2;
    lightboxPrev.hidden = single;
    lightboxNext.hidden = single;
    lightbox.showModal();
  }

  closeLightbox() {
    this.refs.lightbox?.close();
  }

  showPrev() {
    this.#stepShot(-1);
  }

  showNext() {
    this.#stepShot(1);
  }

  /** @param {number} delta */
  #stepShot(delta) {
    const count = this.#shots.length;
    if (count < 2) return;
    this.#shotIndex = (this.#shotIndex + delta + count) % count;
    this.#renderShot();
  }

  #renderShot() {
    const shot = this.#shots[this.#shotIndex];
    if (!shot) return;
    const { lightboxImage, lightboxLabel, lightboxDownload } = this.refs;
    const label = shot.dataset.hofLabel ?? '';
    lightboxImage.src = shot.dataset.hofFull ?? '';
    lightboxImage.alt = label;
    lightboxLabel.textContent = label;
    lightboxDownload.dataset.hofDownloadUrl = shot.dataset.hofFull ?? '';
    lightboxDownload.dataset.hofDownloadName = shot.dataset.hofName ?? 'hall-of-fame.jpg';
  }

  /** @param {KeyboardEvent} event */
  #onLightboxKeydown = (event) => {
    if (event.key === 'ArrowRight') this.showNext();
    if (event.key === 'ArrowLeft') this.showPrev();
  };

  async downloadCurrent() {
    const trigger = this.refs.lightboxDownload;
    const url = trigger.dataset.hofDownloadUrl;
    const filename = trigger.dataset.hofDownloadName || 'hall-of-fame.jpg';

    if (!url || trigger.getAttribute('aria-busy') === 'true') return;

    trigger.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch banner: ${response.status}`);

      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
      window.open(url, '_blank', 'noopener');
    } finally {
      trigger.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('hall-of-fame-component')) {
  customElements.define('hall-of-fame-component', HallOfFameComponent);
}
